"use strict";
// ============================================
// BASE COLOR CONSTANTS (defined once, referenced everywhere)
// ============================================
Object.defineProperty(exports, "__esModule", { value: true });
exports.HHNTheme = exports.HHNDarkTheme = exports.HHNLightTheme = exports.deriveHighlight = exports.LOCKED_SURFACES = exports.sharedLupaColors = exports.sharedStatusColors = exports.sharedDocumentColors = void 0;
// MD3 Error Palette
const error = "#d45858"; // softer red - badges, icons, text
const success = "#61a758";
const statusActive = "#5a8a42"; // Acknowledged/active states
const warning = "#E65100"; // warning text/icons
const discharged = "#C8A200"; // yellow - discharge badges (white text)
// Connection Status
const connected = "#2E7D32"; // Green - online
const disconnected = "#C62828"; // Red - offline
// Primary (Cyan/Teal Brand)
const primary = "#2696be";
// ============================================
// SHARED COLORS - Same in both light and dark themes.
// Add colors here that should NOT change between themes.
// ============================================
const sharedColors = {
    primary,
    error,
    success,
    // Warning colors
    warning,
    // Status colors - patient group colors
    status: {
        active: statusActive,
        pendingSoc: error,
        discharged,
        hospitalized: "#2398bc",
        nonAdmit: "#727172",
        endOfCare: "#52525b",
    },
    // Calendar route map - shared by web and mobile so the two stay in step.
    // Endpoints read as go/stop. Intermediate stops take mobile's pink rather
    // than a patient-status colour, so a pin never implies a patient state.
    route: {
        start: "#4CAF50",
        end: "#F44336",
        stop: "#7D5260",
        line: primary,
    },
    // LUPA status colors
    lupa: {
        met: "#a5d7a7",
        partiallyMet: "#c9eaf5",
        notMet: "#ffcb7f",
        notAvailable: "#b7b5b5",
    },
    // Document type colours are locked: the pairing identifies a file type, so it
    // reads the same in both themes and the ink is scoped by the surface instead.
    document: {
        pdf: { icon: error, background: "#F9DEDC" },
        xls: { icon: "#19754B", background: "#bbf6bb" },
        doc: { icon: primary, background: "#c5e7f3" },
        default: { icon: primary, background: "#e4eaed" },
    },
    // Message visibility colors - same pastel in both themes
    message: {
        visibleToPatient: "#d1c4e9",
        visibleToPhysician: "#b3e5fc",
        visibleToPatientAndPhysician: "#b2dfdb",
        reminder: "#c7e0ff",
        infoBubble: "#2696be33",
    },
    // Literal color constants (NOT semantic - white means white in both themes)
    common: {
        black: "#000000",
        white: "#ffffff",
        red: error,
        green: statusActive,
        orange: warning,
    },
};
// ===== LIGHT THEME SPECIFIC =====
const lightSpecific = {
    onPrimary: "#ffffff",
    primaryInk: "#16576e",
    primaryContainer: "#c5e7f3",
    onPrimaryContainer: "#001f29",
    primaryBackground: "#eaeaea",
    background: "#ffffff",
    contentBackground: "#f1f5f8",
    secondary: "#5c8c99",
    onSecondary: "#ffffff",
    secondaryContainer: "#c9e0e8",
    onSecondaryContainer: "#0a2329",
    elevatedSurface: "#ffffff",
    errorContainer: "#F9DEDC",
    onErrorContainer: "#410E0B",
    warningContainer: "#FFF3E0",
    successContainer: "#bbf6bb",
    expired: "#e6e6e6",
    tabsBackground: "#f3f3f3",
    notesBackground: "#d7eaf2",
    surface: "rgb(251, 252, 254)",
    surfaceVariant: "#e4eaed",
    onSurfaceVariant: "#44474e",
    outline: "rgb(112, 120, 125)",
    outlineVariant: "rgb(192, 200, 205)",
    disabledText: "#52525b",
    // Was #27272a - a dark fill in the light theme, which is why a disabled input
    // read as a black slab on a light composer.
    disabledBackground: "#e4e4e7",
    onDisabledBackground: "#52525b",
    deadSpaceBackground: "#E3E6E6",
    skeletonBackground: "#F699CD",
    notificationStatus: {
        success: "#bbf6bb",
        error: "#F9DEDC",
        info: "#c5ecfd",
    },
    pendingInk: "#a36100",
    successInk: "#39843c",
    elevationLadder: {
        level0: "transparent",
        level1: "#f7f9fc",
        level2: "#f3f6fa",
        level3: "#eef2f8",
        level4: "#eaeff6",
        level5: "#e5ebf4",
    },
    connection: {
        connected: "#1b5e20",
        connecting: "#8a3d00",
        disconnected: "#a01b1b",
    },
    statusInk: {
        active: statusActive,
        pendingSoc: error,
        discharged,
        hospitalized: "#2398bc",
        nonAdmit: "#727172",
        endOfCare: "#52525b",
    },
    message: {
        messageBackground: "#f1f5f8",
        deleted: "#e0e0e0",
        replyBubbleHeader: "#DCECF4",
        replyBubbleMediaBackground: "#00000040",
    },
};
// ===== DARK THEME SPECIFIC =====
const darkSpecific = {
    onPrimary: "#ffffff",
    primaryInk: primary,
    primaryContainer: "#1a3a4a",
    onPrimaryContainer: "#c5e7f3",
    primaryBackground: "#1e2130",
    background: "#0f1117",
    contentBackground: "#141620",
    secondary: "#88b3bf",
    onSecondary: "#ffffff",
    secondaryContainer: "#1e3540",
    onSecondaryContainer: "#c9e0e8",
    elevatedSurface: "#303340",
    errorContainer: "#3d1c1c",
    onErrorContainer: "#F9DEDC",
    warningContainer: "#3d2000",
    successContainer: "#1a3518",
    expired: "#2a2d36",
    tabsBackground: "#1e2130",
    notesBackground: "#152535",
    surface: "#1a1d27",
    surfaceVariant: "#252834",
    onSurfaceVariant: "#9a9da8",
    outline: "rgb(90, 95, 105)",
    outlineVariant: "rgb(55, 60, 70)",
    disabledText: "#6b6e78",
    disabledBackground: "#3a3a3a",
    onDisabledBackground: "#e6e1e5",
    deadSpaceBackground: "#0c0e14",
    skeletonBackground: "#3d1c3a",
    notificationStatus: {
        success: "#1a3518",
        error: "#3d1c1c",
        info: "#152535",
    },
    pendingInk: "#FF9800",
    successInk: success,
    elevationLadder: {
        level0: "transparent",
        level1: "#1f222d",
        level2: "#242732",
        level3: "#292c38",
        level4: "#2e3140",
        level5: "#333747",
    },
    connection: {
        connected: success,
        connecting: "#e3a74e",
        disconnected: "#e26f6f",
    },
    // Only the two darkest statuses move. The rest clear their chip as they are.
    statusInk: {
        active: statusActive,
        pendingSoc: error,
        discharged,
        hospitalized: "#2398bc",
        nonAdmit: "#979697",
        endOfCare: "#8e8e99",
    },
    message: {
        messageBackground: "#141620",
        deleted: "#2a2d36",
        replyBubbleHeader: "#1a2a38",
        replyBubbleMediaBackground: "#00000060",
    },
};
// ============================================
// LOCKED SURFACES
// ============================================
// A locked surface keeps one hex in both themes because the colour itself carries
// meaning to a clinician. That makes the surface a fixed ground, so everything drawn
// on it has to resolve against the surface rather than against the active theme.
// The document palette is locked: the pairing identifies a file type, so it is the
// same in both themes and consumers can read it without a theme at all.
exports.sharedDocumentColors = sharedColors.document;
// Patient status hues are locked too - they identify a state, not a surface.
exports.sharedStatusColors = sharedColors.status;
// LUPA pastels are locked fills; their ink comes from the locked-surface contract.
exports.sharedLupaColors = sharedColors.lupa;
exports.LOCKED_SURFACES = {
    visibleToPatient: sharedColors.message.visibleToPatient,
    visibleToPhysician: sharedColors.message.visibleToPhysician,
    visibleToPatientAndPhysician: sharedColors.message.visibleToPatientAndPhysician,
    // The media preview paints black in both themes, so it is the mirror case: a
    // locked ground whose chrome has to resolve light-on-dark regardless of theme.
    mediaCanvas: sharedColors.common.black,
    // The "unsupported message" bubble: a fixed mustard that never flipped while the
    // text on it did.
    fallbackMessage: "#ceab1b",
    documentChip: sharedColors.document.default.background,
    documentChipPdf: sharedColors.document.pdf.background,
    documentChipXls: sharedColors.document.xls.background,
    documentChipDoc: sharedColors.document.doc.background,
};
// A highlight on a locked surface is derived from its host. No global token works:
// the light primaryContainer is 1.26:1 against the lavender bubble and the dark one
// is 1.45:1 under the ink the surface forces.
const deriveHighlight = (hex, amount = 0.12) => {
    const value = hex.replace("#", "");
    const full = value.length === 3
        ? value
            .split("")
            .map(c => c + c)
            .join("")
        : value;
    const channel = (offset) => {
        const value = parseInt(full.slice(offset, offset + 2), 16);
        // A negative amount lightens, for grounds that are already dark.
        const next = amount >= 0 ? value * (1 - amount) : value + (255 - value) * -amount;
        return Math.max(0, Math.min(255, Math.round(next)));
    };
    return `#${[0, 2, 4]
        .map(offset => channel(offset).toString(16).padStart(2, "0"))
        .join("")}`;
};
exports.deriveHighlight = deriveHighlight;
// ============================================
// MERGED THEME EXPORTS
// ============================================
function buildHHNTheme(specific) {
    return Object.assign(Object.assign(Object.assign({}, sharedColors), specific), { 
        // Merge nested objects (shared + theme-specific parts)
        message: Object.assign(Object.assign({}, sharedColors.message), specific.message) });
}
exports.HHNLightTheme = buildHHNTheme(lightSpecific);
exports.HHNDarkTheme = buildHHNTheme(darkSpecific);
// Backward-compatible alias for web-app and other consumers
exports.HHNTheme = exports.HHNLightTheme;
